/* ============================================================
   Bundle Picker — JavaScript
   Handles: slot selection, product modal, cart add, totals
   ============================================================ */

(function () {
  'use strict';

  // ── Shared localStorage keys ──────────────────────────────
  const BACKUP_KEY   = 'bundle_cart_backup';
  const PROGRESS_KEY = 'bundle_checkout_in_progress';

  function clearBundleFlags() {
    localStorage.removeItem(BACKUP_KEY);
    localStorage.removeItem(PROGRESS_KEY);
  }

  // ── Shared cart restore function ──────────────────────────
  // Restores the pre-bundle original cart silently (no page reload).
  // Called directly from the iframe-close handlers so the user never
  // sees a full page refresh. Also called on page load as a safety net
  // in case Shiprocket performed its own redirect before we could
  // intercept the close event.
  async function restoreOriginalCart() {
    const inProgress = localStorage.getItem(PROGRESS_KEY);
    if (!inProgress) return;

    const backup = localStorage.getItem(BACKUP_KEY);
    if (!backup) { clearBundleFlags(); return; }

    let originalItems;
    try {
      originalItems = JSON.parse(backup);
    } catch (e) {
      clearBundleFlags();
      return;
    }

    try {
      await fetch('/cart/clear.js', { method: 'POST' });
      if (originalItems.length > 0) {
        const addResp = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ items: originalItems })
        });
        if (!addResp.ok) throw new Error('cart/add.js failed: ' + addResp.status);
      }
      // Only remove flags after a confirmed successful restore
      clearBundleFlags();
      console.log('[BundlePicker] Original cart restored successfully.');

      // Refresh the cart icon count bubble without a page reload
      refreshCartUI();
    } catch (e) {
      // Leave flags intact so the next page load can retry
      console.warn('[BundlePicker] Cart restore failed, will retry on next load:', e);
    }
  }

  // ── Refresh cart count bubble in-place ───────────────────
  // Fetches the current cart and updates the header icon count
  // without triggering a full page reload.
  async function refreshCartUI() {
    try {
      const resp = await fetch('/cart.js');
      const cart = await resp.json();
      const count = cart.item_count || 0;

      // Update every count bubble the Dawn / Spotlight theme renders
      document.querySelectorAll('.cart-count-bubble').forEach(function (bubble) {
        bubble.querySelectorAll('span').forEach(function (s) {
          if (!s.classList.contains('visually-hidden')) s.textContent = count;
        });
        bubble.classList.toggle('hidden', count === 0);
      });

      // Let any other theme JS listeners know the cart changed
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount: count } }));
    } catch (e) {
      console.warn('[BundlePicker] Could not refresh cart UI:', e);
    }
  }

  // ── Safety-net restore on page load ──────────────────────
  // Runs only when the PROGRESS_KEY flag is still set on load, which
  // means Shiprocket redirected away before our close-event handler
  // could call restoreOriginalCart() inline.
  restoreOriginalCart();

  // Wait for DOM ready
  document.addEventListener('DOMContentLoaded', function () {
    const picker = document.getElementById('bundle-picker');
    if (!picker) return; // not on a bundle collection page

    // ── State ──────────────────────────────────────────────
    const slots = [null, null, null]; // each slot holds a product object or null
    let activeSlot = null;           // which slot index is being filled (0, 1, 2)
    const STORAGE_KEY = 'bundle_picker_slots';

    // ── Config from data attributes ────────────────────────
    const bundlePrice = parseInt(picker.dataset.bundlePrice, 10) || 0;
    const currencySymbol = picker.dataset.currency || '₹';

    // ── Load products from embedded JSON ──────────────────
    let allProducts = [];
    try {
      const dataEl = document.getElementById('bundle-products-data');
      if (dataEl) allProducts = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.warn('[BundlePicker] Could not parse products JSON', e);
    }

    // ── DOM refs ──────────────────────────────────────────
    const modal = document.getElementById('bundle-product-modal');
    const backdrop = document.getElementById('bundle-backdrop');
    const productList = document.getElementById('bundle-product-list');
    const searchInput = document.getElementById('bundle-product-search');
    const closeBtn = document.getElementById('bundle-modal-close');
    const buyBtn = document.getElementById('bundle-buy-btn');
    const totalEl = document.getElementById('bundle-total');
    const countEl = document.getElementById('bundle-count');
    const toast = document.getElementById('bundle-toast');
    const notifyEl = document.getElementById('bundle-notify');

    // ── Helpers ───────────────────────────────────────────

    function formatMoney(cents) {
      const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
      return currencySymbol + amount;
    }

    function getSlotEl(index) {
      return document.querySelector(`.bundle-slot[data-slot="${index + 1}"]`);
    }

    function getSlotBtn(index) {
      return document.querySelector(`[data-slot-trigger="${index + 1}"]`);
    }

    function getSlotRemove(index) {
      return document.querySelector(`[data-slot-remove="${index + 1}"]`);
    }

    // ── Render a slot as filled ───────────────────────────

    function fillSlot(index, product) {
      slots[index] = product;
      const slotEl = getSlotEl(index);
      const btn = getSlotBtn(index);
      const removeBtn = getSlotRemove(index);
      const plusEl = slotEl.querySelector('.bundle-slot__plus');
      const imgEl = slotEl.querySelector('.bundle-slot__img');

      btn.classList.add('is-filled');
      plusEl.style.display = 'none';

      if (product.image) {
        imgEl.src = product.image;
        imgEl.alt = product.title;
        imgEl.style.display = 'block';
      }

      removeBtn.style.display = 'flex';

      updateMeta();
      saveSlots();
    }

    // ── Clear a slot ──────────────────────────────────────

    function clearSlot(index) {
      slots[index] = null;
      const slotEl = getSlotEl(index);
      const btn = getSlotBtn(index);
      const removeBtn = getSlotRemove(index);
      const plusEl = slotEl.querySelector('.bundle-slot__plus');
      const imgEl = slotEl.querySelector('.bundle-slot__img');

      btn.classList.remove('is-filled');
      plusEl.style.display = '';
      imgEl.style.display = 'none';
      imgEl.src = '';
      removeBtn.style.display = 'none';

      updateMeta();
      saveSlots();
    }

    // ── sessionStorage persistence ──────────────────────────

    function saveSlots() {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(slots));
      } catch (e) { /* storage unavailable */ }
    }

    function loadSlots() {
      try {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        if (!saved) return;
        const parsed = JSON.parse(saved);
        if (!Array.isArray(parsed) || parsed.length !== 3) return;
        parsed.forEach(function (product, i) {
          if (product && product.variant_id) fillSlot(i, product);
        });
      } catch (e) { /* storage unavailable or corrupt */ }
    }

    function clearSavedSlots() {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { }
    }

    // ── Update counter, total, buy button ─────────────────

    function updateMeta() {
      const filled = slots.filter(Boolean).length;
      countEl.textContent = filled;

      // Running total = sum of selected product prices
      const sumCents = slots.reduce((acc, p) => acc + (p ? p.price : 0), 0);
      totalEl.textContent = formatMoney(sumCents);

      // Enable buy button only when all 3 slots filled
      buyBtn.disabled = filled < 3;

      // Show unlock notification strip when all 3 filled
      if (notifyEl) {
        notifyEl.style.display = filled === 3 ? 'block' : 'none';
      }
    }

    // ── Open product picker modal ─────────────────────────

    function openModal(slotIndex) {
      activeSlot = slotIndex;
      renderProductList('');
      modal.style.display = 'flex';
      backdrop.style.display = 'block';
      searchInput.value = '';
      searchInput.focus();
    }

    // ── Close product picker modal ────────────────────────

    function closeModal() {
      modal.style.display = 'none';
      backdrop.style.display = 'none';
      activeSlot = null;
    }

    // ── Render product list inside modal ──────────────────

    function renderProductList(query) {
      const q = query.toLowerCase().trim();
      const selectedVariantIds = slots.filter(Boolean).map(p => p.variant_id);

      const filtered = allProducts.filter(p =>
        p.title.toLowerCase().includes(q)
      );

      productList.innerHTML = '';

      if (filtered.length === 0) {
        productList.innerHTML = '<li style="padding:1rem;color:#888;text-align:center;">No products found</li>';
        return;
      }

      filtered.forEach(function (product) {
        const isSelected = selectedVariantIds.includes(product.variant_id);
        const li = document.createElement('li');
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', isSelected ? 'true' : 'false');

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bundle-picker__product-item' + (isSelected ? ' is-selected' : '');

        // Thumbnail
        const thumbWrap = document.createElement('div');
        thumbWrap.className = 'bundle-picker__product-thumb';
        if (product.image) {
          const img = document.createElement('img');
          img.src = product.image;
          img.alt = product.title;
          img.loading = 'lazy';
          img.style.width = '100%';
          img.style.height = '100%';
          img.style.objectFit = 'cover';
          img.style.borderRadius = '10px';
          thumbWrap.appendChild(img);
        } else {
          thumbWrap.classList.add('bundle-picker__product-thumb--placeholder');
          thumbWrap.textContent = '🛍️';
        }

        // Info
        const info = document.createElement('div');
        info.className = 'bundle-picker__product-info';

        const name = document.createElement('p');
        name.className = 'bundle-picker__product-name';
        name.textContent = product.title;

        const price = document.createElement('p');
        price.className = 'bundle-picker__product-price';
        price.textContent = product.price_formatted;

        info.appendChild(name);
        info.appendChild(price);
        btn.appendChild(thumbWrap);
        btn.appendChild(info);

        if (isSelected) {
          const tag = document.createElement('span');
          tag.textContent = '✓ Added';
          tag.style.cssText = 'font-size:0.75rem;color:#9B7BBB;font-weight:700;flex-shrink:0;';
          btn.appendChild(tag);
        }

        btn.addEventListener('click', function () {
          if (isSelected) return;
          fillSlot(activeSlot, product);
          closeModal();
        });

        li.appendChild(btn);
        productList.appendChild(li);
      });
    }

    // ── Add bundle to cart ────────────────────────────────

    async function addBundleToCart() {
      if (slots.some(s => !s)) return;

      buyBtn.classList.add('is-loading');
      buyBtn.disabled = true;

      const bundleItems = slots.map(product => ({
        id: product.variant_id,
        quantity: 1,
        properties: { '_bundle': 'true' }
      }));

      try {
        // ── 1. Snapshot original cart → localStorage ──────────
        // We exclude bundle items themselves from the backup so a partial
        // add doesn't double-count them on restore.
        const cartResp = await fetch('/cart.js');
        const currentCart = await cartResp.json();
        // Exclude ALL bundle-tagged items from the backup — not just the
        // current slot selection. This prevents a stale bundle item (from a
        // previous checkout attempt with different products) from slipping
        // into the backup and being restored later.
        const originalItems = currentCart.items
          .filter(item => !(item.properties && item.properties['_bundle'] === 'true'))
          .map(item => ({
            id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties
          }));

        // Use localStorage — more reliable than sessionStorage across the
        // redirects that Shiprocket can trigger.
        localStorage.setItem('bundle_cart_backup', JSON.stringify(originalItems));
        // Flag signals that a bundle checkout was started; cleared on success
        // OR if Shiprocket signals a completed order.
        localStorage.setItem('bundle_checkout_in_progress', '1');

        // ── 2. Clear cart ──────────────────────────────────────
        await fetch('/cart/clear.js', { method: 'POST' });

        // ── 3. Add ONLY bundle items ───────────────────────────
        const addResp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: bundleItems })
        });
        if (!addResp.ok) throw new Error('Cart add failed');

        // ── 4. Detect Shiprocket close → restore or skip ───────
        // • If the user CLOSES the iframe without purchasing → call
        //   restoreOriginalCart() directly (no page reload).
        // • If Shiprocket fires an ORDER_PLACED / success event → clear
        //   the backup flags (order placed, cart should stay empty).
        const SR_SEL = [
          'iframe[src*="shiprocket"]', 'iframe[src*="fastrr"]', 'iframe[src*="pickrr"]',
          '#shiprocket-checkout-iframe', '[class*="fastrr"]', '#shiprocket-checkout'
        ].join(',');

        let modalHasOpened = false;
        let orderWasPlaced = false;

        const checkInterval = setInterval(function () {
          const els = Array.from(document.querySelectorAll(SR_SEL));
          const isVisible = els.some(
            function (el) {
              return el.offsetWidth > 0 && el.offsetHeight > 0 && getComputedStyle(el).display !== 'none';
            }
          );
          if (isVisible) {
            modalHasOpened = true;
          } else if (modalHasOpened && !isVisible) {
            clearInterval(checkInterval);
            window.removeEventListener('message', onSrMessage);
            if (orderWasPlaced) {
              // Purchase completed — clear flags, leave cart empty.
              clearBundleFlags();
            } else {
              // User closed without buying — restore original cart in-place.
              restoreOriginalCart();
            }
          }
        }, 500);

        function onSrMessage(e) {
          const d = e.data || {};
          const type   = d.type   || '';
          const event  = d.event  || '';
          const action = d.action || '';

          // Detect a successful purchase event from Shiprocket
          if (
            type  === 'ORDER_PLACED'    || type  === 'CHECKOUT_SUCCESS' ||
            event === 'order_placed'    || event === 'checkout_success' ||
            action === 'order_placed'   || action === 'checkout_success'
          ) {
            orderWasPlaced = true;
            clearBundleFlags();
          }

          // Detect iframe close (user dismissed without buying)
          if (
            (type === 'CHECKOUT_CLOSED' || event === 'close' || action === 'close') &&
            !orderWasPlaced
          ) {
            clearInterval(checkInterval);
            window.removeEventListener('message', onSrMessage);
            // Restore original cart silently — no page reload needed.
            restoreOriginalCart();
          }
        }
        window.addEventListener('message', onSrMessage);

        // ── 5. Open Shiprocket checkout ────────────────────────
        clearSavedSlots();
        if (window.shiprocketCheckoutEvents && typeof window.shiprocketCheckoutEvents.buyCart === 'function') {
          window.shiprocketCheckoutEvents.buyCart(null);
        } else {
          window.location.href = '/checkout';
        }

      } catch (err) {
        // Clean up both flags so a partial failure doesn't corrupt the cart
        // on the next page load.
        localStorage.removeItem('bundle_cart_backup');
        localStorage.removeItem('bundle_checkout_in_progress');
        console.error('[BundlePicker] Error:', err);
        showToast('❌ Something went wrong. Please try again.');
        buyBtn.classList.remove('is-loading');
        buyBtn.disabled = false;
      }
    }



    // ── Reset all slots ───────────────────────────────────

    function resetPicker() {
      [0, 1, 2].forEach(i => clearSlot(i));
      updateMeta();
      updateBuyBtnLabel();
    }

    // ── Update buy button label ───────────────────────────
    // Label is static in HTML; only re-enable/disable state matters

    // ── Update cart count bubble in header ────────────────

    function updateCartCount(count) {
      const bubbles = document.querySelectorAll(
        '.cart-count-bubble span[aria-hidden], #cart-icon-bubble .cart-count-bubble'
      );
      bubbles.forEach(el => {
        el.textContent = count;
      });

      // Dawn/Spotlight theme cart bubble
      const cartBubble = document.querySelector('.cart-count-bubble');
      if (cartBubble) {
        cartBubble.querySelectorAll('span').forEach(s => {
          if (!s.classList.contains('visually-hidden')) s.textContent = count;
        });
        if (count > 0) cartBubble.classList.remove('hidden');
      }

      // Dispatch event for theme JS listeners
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { itemCount: count }
      }));
    }

    // ── Show toast ────────────────────────────────────────

    function showToast(message) {
      toast.textContent = message;
      toast.style.display = 'block';
      clearTimeout(toast._timer);
      toast._timer = setTimeout(function () {
        toast.style.display = 'none';
      }, 3000);
    }

    // ── Event Listeners ───────────────────────────────────

    // Slot [+] buttons
    document.querySelectorAll('[data-slot-trigger]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const slotIndex = parseInt(btn.dataset.slotTrigger, 10) - 1;
        openModal(slotIndex);
      });
    });

    // Slot remove (✕) buttons
    document.querySelectorAll('[data-slot-remove]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        const slotIndex = parseInt(btn.dataset.slotRemove, 10) - 1;
        clearSlot(slotIndex);
      });
    });

    // Modal close button
    closeBtn.addEventListener('click', closeModal);

    // Backdrop click closes modal
    backdrop.addEventListener('click', closeModal);

    // Escape key closes modal
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeModal();
    });

    // Search input filter
    searchInput.addEventListener('input', function () {
      renderProductList(this.value);
    });

    // BUY BUNDLE button
    buyBtn.addEventListener('click', addBundleToCart);

    // ── "Add to Bundle" card buttons ─────────────────────
    // These are outside #bundle-picker so we listen on document
    document.querySelectorAll('[data-bundle-add]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const product = {
          id: parseInt(btn.dataset.productId, 10),
          variant_id: parseInt(btn.dataset.variantId, 10),
          title: btn.dataset.productTitle,
          price: parseInt(btn.dataset.productPrice, 10),
          price_formatted: btn.dataset.productPriceFormatted,
          image: btn.dataset.productImage
        };

        // Find the first empty slot
        const emptySlot = slots.findIndex(function (s) { return s === null; });

        if (emptySlot === -1) {
          showToast('Bundle is full! Remove a product to swap it.');
          return;
        }

        fillSlot(emptySlot, product);

        // Visual feedback on the card button
        const original = btn.textContent;
        btn.textContent = '✓ Added to Bundle';
        btn.classList.add('is-added');
        setTimeout(function () {
          btn.textContent = original;
          btn.classList.remove('is-added');
        }, 2000);

        // Scroll to bundle widget smoothly
        picker.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

        showToast('✅ Added to bundle! (' + (slots.filter(Boolean).length) + '/3)');
      });
    });

    // ── Init ──────────────────────────────────────────────
    loadSlots();    // restore from sessionStorage if page was refreshed
    updateMeta();

  }); // DOMContentLoaded

  // ── Avada Sales Pop — reposition to top ───────────────
  // Mirrors the cart-drawer toggleAvadaPop pattern.
  // Avada keeps writing inline bottom styles via its own JS,
  // so a MutationObserver is the only reliable way to override it.
  (function () {
    const AVADA_SEL = '#Avada_SalesPopV2, [id*="Avada_SalesPop"], .avada-sp__container, .avada-sales-pop, .avada-sp-wrapper';

    function repositionAvadaToTop() {
      document.querySelectorAll(AVADA_SEL).forEach(function (pop) {
        pop.style.setProperty('bottom', 'auto', 'important');
        pop.style.setProperty('top', '12px', 'important');
        pop.style.setProperty('left', '12px', 'important');
        pop.style.setProperty('right', 'auto', 'important');
      });
    }

    // Run once on load
    document.addEventListener('DOMContentLoaded', repositionAvadaToTop);

    // Watch for Avada re-injecting or mutating its inline styles
    const avadaObserver = new MutationObserver(repositionAvadaToTop);
    if (document.body) {
      avadaObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }
  })();

})();
